import { NotificationDTO } from '@/api/DTO/notification/notification.interface';
import {
	useBatchReadNotificationsMutation,
	useBatchUnreadNotificationsMutation,
	useGetNotificationListQuery,
} from '@/api/notification/notificationApiSlice';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import {
	notificationDetailPath,
	UNREAD_ACCENT_BAR,
	UNREAD_BACKGROUND,
	UNREAD_BACKGROUND_HOVER,
} from '@/common/components/notification/notificationHelper';
import { DISPLAY_DATE_TIME_FORMAT } from '@/utils/dateHelper';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import MarkunreadOutlinedIcon from '@mui/icons-material/MarkunreadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
	Box,
	Button,
	Card,
	CardContent,
	IconButton,
	Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import qs from 'qs';
import { useEffect, useMemo, useState } from 'react';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import NotificationSummaryPageFilterSection from './components/NotificationSummaryPageFilterSection';
import {
	NotificationReadStatus,
	notificationListFilter,
	NotificationSummaryFilter,
} from './notificationSummaryFilter';

/**
 * Unread rows get a tint plus a bar down their left edge, rather than a dot.
 *
 * Once the checkbox column exists a dot is no longer the first thing on the row, so
 * it stops reading as a status marker; the bar sits at the true row edge and takes
 * that job back. The popover draws the same bar, from the same constants.
 *
 * Bold subject carries the same signal without relying on colour, so the state
 * survives greyscale and colour blindness.
 */
const UNREAD_ROW_CLASS = 'notification-row--unread';

/**
 * Full history of the notifications sent to the logged-in staff member.
 *
 * The bell popover only previews the newest few; this is where older ones are
 * found, by subject search and read-state filter.
 *
 * Rows can be selected and marked read in bulk, the way an email client does.
 * Without it, a staff member who acts on an alert without opening it leaves that
 * notification unread forever, and the bell badge drifts into permanent noise.
 */
export default function NotificationSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);

	const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
		type: 'include',
		ids: new Set(),
	});

	const defaultPropertyFilter: NotificationSummaryFilter = {
		pagination: initialPagination,
		sortings: [],
		searchString: '',
		readStatus: NotificationReadStatus.all,
	};
	const defaultValues: DefaultValues<NotificationSummaryFilter> =
		defaultPropertyFilter;
	const methods = useForm<NotificationSummaryFilter>({ defaultValues });

	const transformedSearchParams: NotificationSummaryFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'readStatus',
		]),
	};

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const filterFormResult = value as NotificationSummaryFilter;

			const cleanedFilter = Object.fromEntries(
				Object.entries(filterFormResult).filter(
					([, val]) => val !== '' && val != null
				)
			);

			setSearchParams(qs.stringify(cleanedFilter));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch search params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, { keepDefaultValues: true });
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const { data: notificationList, isSuccess: isGetNotificationListSuccess } =
		useGetNotificationListQuery(
			notificationListFilter(transformedSearchParams),
			{ refetchOnMountOrArgChange: true }
		);

	const pagedRows = useMemo(
		() => notificationList?.data?.items ?? [],
		[notificationList]
	);

	const [batchReadNotifications] = useBatchReadNotificationsMutation();
	const [batchUnreadNotifications] = useBatchUnreadNotificationsMutation();

	/**
	 * The selected rows, split by read state.
	 *
	 * Resolved against the CURRENT PAGE only, matching how selection behaves
	 * elsewhere in the portal. The grid reports a selection either as the rows that
	 * ARE selected (`type: 'include'`) or, after the header checkbox, as the rows
	 * that are NOT (`type: 'exclude'`); scoping both to `pagedRows` keeps the header
	 * checkbox meaning "this page" rather than every row matching the filter, which
	 * could silently be hundreds.
	 */
	const selection = useMemo(() => {
		const { type, ids } = selectionModel;
		const selected = pagedRows.filter((row) =>
			type === 'include' ? ids.has(row.id) : !ids.has(row.id)
		);
		return {
			count: selected.length,
			unreadIds: selected.filter((row) => !row.isRead).map((row) => row.id),
			readIds: selected.filter((row) => row.isRead).map((row) => row.id),
		};
	}, [selectionModel, pagedRows]);

	/**
	 * One button that flips direction with the selection, as email clients do:
	 * any unread in the selection means the action is "Mark as read", and it only
	 * offers "Mark as unread" once everything selected has already been read. That
	 * gives a mixed selection one unambiguous meaning, and it acts on just the rows
	 * it would change so a rough selection can be dragged without care.
	 */
	const marksAsRead = selection.unreadIds.length > 0;

	const clearSelection = () =>
		setSelectionModel({ type: 'include', ids: new Set() });

	// selection is per page, so changing page, page size, search or filter starts
	// fresh — otherwise a selection made on page 1 lingers invisibly on page 2 and
	// the action would hit rows the staff member can no longer see
	useEffect(() => {
		clearSelection();
	}, [
		transformedSearchParams.pagination.page,
		transformedSearchParams.pagination.size,
		transformedSearchParams.searchString,
		transformedSearchParams.readStatus,
	]);

	// The whole selection goes over either way: already-read ids are harmless to
	// batch-read, and the endpoint invalidates the list and the badge itself, so a
	// partially applied batch still ends up showing the server's real state.
	const handleToggleReadState = () => {
		if (marksAsRead)
			batchReadNotifications({ notificationIds: selection.unreadIds });
		else batchUnreadNotifications({ notificationIds: selection.readIds });
		clearSelection();
	};

	const columns: GridColDef<NotificationDTO>[] = [
		{
			field: 'title',
			headerName: t('notification.subject'),
			flex: 60,
			renderCell: (params) => (
				<Typography
					sx={{
						fontSize: 14,
						fontWeight: params.row.isRead ? 400 : 700,
						color: 'common.neutral20',
						whiteSpace: 'normal',
						wordBreak: 'break-word',
					}}
				>
					{/* the bar is drawn in CSS, so screen readers need the state spelled out */}
					{!params.row.isRead && (
						<Box component='span' sx={visuallyHidden}>
							{t('notification.statusUnread')}
						</Box>
					)}
					{params.row.title}
				</Typography>
			),
		},
		{
			field: 'createdAt',
			headerName: t('notification.dateIssued'),
			flex: 22,
			renderCell: (params) =>
				dayjs(params.row.createdAt)
					.locale('en')
					.format(DISPLAY_DATE_TIME_FORMAT),
		},
		{
			field: 'view',
			headerName: '',
			width: 68,
			sortable: false,
			renderCell: (params) => (
				<Link to={notificationDetailPath(params.row.id)}>
					<IconButton color='secondary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>{t('notification.title')}</Typography>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						{/*
							The action rides in the spare half of the filter row and appears
							only once rows are picked. A permanently greyed button that never
							says why it is grey teaches nothing, and the always-visible
							checkbox column is what makes the action discoverable anyway.
						*/}
						<NotificationSummaryPageFilterSection
							action={
								selection.count > 0 && (
									<>
										<Typography
											sx={{ fontSize: 13, color: 'common.neutral50' }}
										>
											{t('notification.selectedCount', {
												count: selection.count,
											})}
										</Typography>
										<Button
											size='small'
											variant='outlined'
											color='info'
											startIcon={
												marksAsRead ? (
													<DraftsOutlinedIcon />
												) : (
													<MarkunreadOutlinedIcon />
												)
											}
											onClick={handleToggleReadState}
										>
											{t(
												marksAsRead
													? 'notification.markAsRead'
													: 'notification.markAsUnread'
											)}
										</Button>
									</>
								)
							}
						/>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetNotificationListSuccess}
								rows={pagedRows}
								rowCount={notificationList?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
								checkboxSelection
								rowSelectionModel={selectionModel}
								onRowSelectionModelChange={(model) => setSelectionModel(model)}
								getRowClassName={(params) =>
									params.row.isRead ? '' : UNREAD_ROW_CLASS
								}
								sx={{
									[`& .${UNREAD_ROW_CLASS}`]: {
										backgroundColor: UNREAD_BACKGROUND,
										boxShadow: UNREAD_ACCENT_BAR,
										'&:hover': { backgroundColor: UNREAD_BACKGROUND_HOVER },
									},
								}}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
